import { makeObservable, computed } from 'mobx';
import toNumber from 'lodash/toNumber';
import { BindingProps, StoreNode } from '../../../store';
import { Store } from '../../../store/store';
import { ChartPageParams } from '../chartSchema';
import { MetricCounterState } from '../metricCounter/metricCounterState';
import { ApiResponse } from '../../../api/apiSchema';
import { parseKeenJsonResult } from '../../../components/chart/utils/parseKeenJsonResult';
import { durationToString } from '../../../core/time';
import { Team } from '../../../entities/team';
import { Error } from '../../../core/error';
import { ApiRequestName } from '../../../api/apiSchema';

type Props = BindingProps<{
  params?: ChartPageParams;
}>

type ValueAccessorResult = [number | null, (Error | string)?];
type ValueAccessor = (apiResult: unknown) => ValueAccessorResult;

function makeValueAccessor<Q extends ApiRequestName>(
  fn: (result: ApiResponse<Q>) => ValueAccessorResult
): ValueAccessor {
  return (apiResult: unknown) => fn(apiResult as ApiResponse<Q>);
}

export class MetricsWrapperState
  extends StoreNode {

  readonly nodeType = 'MetricsWrapperState';

  constructor(store: Store, props: Props) {
    super(store, props);
    makeObservable(this);
  }

  @computed get queryParams(): ChartPageParams {
    return this.getResolvedProp('params') ?? null;
  }

  @computed get teamId(): string | null {
    return this.queryParams?.teamId || null;
  }
  @computed get team(): Team | null {
    return this.store.teamManager.getTeam(this.teamId);
  }

  readonly metrics = [
    new MetricCounterState(this.store, {
      label: 'Uploads',
      queryName: 'keen_getTotalUniqueUploads',
      queryParams: () => (this.queryParams),
      valueAccessor: makeValueAccessor<'keen_getTotalUniqueUploads'>((result) => {
        const apiResultStr = result.keenBatch.totalUniqueUploads?.result;
        const [apiResult, parseErr] = parseKeenJsonResult<number>(apiResultStr);
        const data = toNumber(apiResult) ?? 0;

        return [data, parseErr];
      }),
      outputData: (data: number) => {
        return data?.toLocaleString(undefined, { maximumFractionDigits: 2 }) ?? null;
      },
    }),
    new MetricCounterState(this.store, {
      label: 'Plays',
      queryName: 'keen_getTotalPlays',
      queryParams: () => (this.queryParams),
      valueAccessor: makeValueAccessor<'keen_getTotalPlays'>((result) => {
        const apiResultStr = result.keenBatch.totalPlays?.result;
        const [apiResult, parseErr] = parseKeenJsonResult<number>(apiResultStr);
        const data = toNumber(apiResult) ?? 0;

        return [data, parseErr];
      }),
      outputData: (data: number) => {
        return data?.toLocaleString(undefined, { maximumFractionDigits: 2 }) ?? null;
      },
    }),
    new MetricCounterState(this.store, {
      label: 'Avg. Watch Time',
      queryName: 'keen_getAvgWatchTime',
      queryParams: () => (this.queryParams),
      valueAccessor: makeValueAccessor<'keen_getAvgWatchTime'>((result) => {
        const apiResultStr = result.keenBatch.avgWatchTime?.result;
        const [apiResult, parseErr] = parseKeenJsonResult<number>(apiResultStr);
        const data = toNumber(apiResult) ?? 0;

        return [data, parseErr];
      }),
      outputData: (data: number) => {
        return data ? durationToString(data, 'time') : null;
      },
    }),
    new MetricCounterState(this.store, {
      label: 'Library Members',
      display: () => (!!this.team),
      outputData: () => {
        if (!this.team)
          return null;

        const {
          members,
          membersCount
        } = this.team;
        const length = membersCount || members?.length || 0;

        return length.toLocaleString(undefined, { maximumFractionDigits: 2 });
      },
    }),
    new MetricCounterState(this.store, {
      label: 'Topics Viewed',
      queryName: 'keen_getTotalTopics',
      queryParams: () => (this.queryParams),
      valueAccessor: makeValueAccessor<'keen_getTotalTopics'>((result) => {
        const apiResultStr = result.keenBatch.totalTopics?.result;
        const [apiResult, parseErr] = parseKeenJsonResult<number>(apiResultStr);
        const data = toNumber(apiResult) ?? 0;

        return [data, parseErr];
      }),
      outputData: (data: number) => {
        return data?.toLocaleString(undefined, { maximumFractionDigits: 2 }) ?? null;
      },
    }),
    new MetricCounterState(this.store, {
      label: 'Reactions',
      queryName: 'keen_getTotalReactions',
      queryParams: () => (this.queryParams),
      valueAccessor: makeValueAccessor<'keen_getTotalReactions'>((result) => {
        const apiResultStr = result.keenBatch.totalReactions?.result;
        const [apiResult, parseErr] = parseKeenJsonResult<number>(apiResultStr);
        const data = toNumber(apiResult) ?? 0;

        return [data, parseErr];
      }),
      outputData: (data: number) => {
        return data?.toLocaleString(undefined, { maximumFractionDigits: 2 }) ?? null;
      },
    }),
    new MetricCounterState(this.store, {
      label: 'Shares',
      queryName: 'keen_getTotalShares',
      queryParams: () => (this.queryParams),
      valueAccessor: makeValueAccessor<'keen_getTotalShares'>((result) => {
        const apiResultStr = result.keenBatch.totalShares?.result;
        const [apiResult, parseErr] = parseKeenJsonResult<number>(apiResultStr);
        const data = toNumber(apiResult) ?? 0;

        return [data, parseErr];
      }),
      outputData: (data: number) => {
        return data?.toLocaleString(undefined, { maximumFractionDigits: 2 }) ?? null;
      },
    }),
    new MetricCounterState(this.store, {
      label: 'Bookmarks',
      queryName: 'keen_getTotalBookmarks',
      queryParams: () => (this.queryParams),
      valueAccessor: makeValueAccessor<'keen_getTotalBookmarks'>((result) => {
        const apiResultStr = result.keenBatch.totalBookmarks?.result;
        const [apiResult, parseErr] = parseKeenJsonResult<number>(apiResultStr);
        const data = toNumber(apiResult) ?? 0;

        return [data, parseErr];
      }),
      outputData: (data: number) => {
        return data?.toLocaleString(undefined, { maximumFractionDigits: 2 }) ?? null;
      },
    }),
  ];

  @computed get outputMetrics(): MetricCounterState[] {
    return this.metrics.filter((metricCounter) => metricCounter.display);
  }

}
